'use client';

import { c as _c } from "react/compiler-runtime";
import { useWallets } from '@morfi/backend-sdk-v2/client';
import { PublicKey } from '@solana/web3.js';
import { useTranslations } from 'next-intl';
import { type FormEvent, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { ModalFooter } from '@/components/ui/modal-footer';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { StepIndicator } from '@/components/ui/step-indicator';
import { TransferInputStep } from '@/components/wallet/transfer-funds-input-step';
import { TransferConfirmationStep, TransferProcessingStep, TransferResultStep } from '@/components/wallet/transfer-funds-status-steps';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { useWalletPicker } from '@/components/wallet/wallet-picker-dropdown';
import { D6_SCALE, USDC_DUST_THRESHOLD, USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import { useTransferUsdc } from '@/hooks/wallet/use-transfer-usdc';
import { useFormattedCurrency } from '@/lib/currency';
import { getActiveNetwork } from '@/lib/network/active';
import { reportError } from '@/lib/telemetry';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useWalletBalances } from '@/queries/wallet-balance';

/** Base58 character set: 1-9, A-H, J-N, P-Z, a-k, m-z (no 0, O, I, l). */
const BASE58_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
function parseLocalAmountD6(value: string, toUsdc: (localAmount: number) => number): bigint | null {
  const localAmount = Number(value);
  if (!Number.isFinite(localAmount) || localAmount <= 0) return null;
  const amountD6 = Math.round(toUsdc(localAmount) * D6_SCALE);
  return Number.isSafeInteger(amountD6) && amountD6 > 0 ? BigInt(amountD6) : null;
}
type DialogStep = 'input' | 'confirm' | 'processing' | 'result';
interface TransferFundsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
function truncateAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
function getExplorerUrl(signature: string): string {
  const network = getActiveNetwork();
  if (network === 'mainnet') {
    return `https://explorer.solana.com/tx/${signature}`;
  }
  return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
}
export function TransferFunds(t0) {
  const $ = _c(112);
  const {
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("wallet.transfer");
  const {
    announce
  } = useAnnouncer();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const {
    currency,
    formatBalance,
    formatInputAmount,
    formatLocalAmount,
    toUsdc
  } = useFormattedCurrency();
  const {
    selectedWalletId,
    setSelectedWalletId,
    selectedWallet
  } = useWalletPicker(open);
  const {
    step: txStep,
    error: txError,
    signature,
    submit,
    reset
  } = useTransferUsdc();
  const [dialogStep, setDialogStep] = useState("input");
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [maxAmountD6, setMaxAmountD6] = useState(null);
  const [inputError, setInputError] = useState(null);
  const selectedAddress = selectedWallet?.address;
  let t1;
  if ($[0] !== selectedAddress) {
    t1 = selectedAddress !== undefined ? [selectedAddress] : [];
    $[0] = selectedAddress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    perWallet,
    perWalletRaw
  } = useWalletBalances(t1);
  const balance = selectedAddress !== undefined ? perWallet.get(selectedAddress) ?? 0 : 0;
  let t2;
  if ($[2] !== perWalletRaw || $[3] !== selectedAddress) {
    t2 = selectedAddress !== undefined ? perWalletRaw.get(selectedAddress) ?? BigInt(0) : BigInt(0);
    $[2] = perWalletRaw;
    $[3] = selectedAddress;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const balanceD6 = t2;
  let t3;
  let t4;
  if ($[5] !== txStep) {
    t3 = () => {
      if (txStep === "confirmed" || txStep === "failed") {
        setDialogStep("result");
      }
    };
    t4 = [txStep];
    $[5] = txStep;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== dialogStep || $[9] !== onOpenChange) {
    t5 = function handleOpenChange(next) {
      if (dialogStep === "processing") {
        return;
      }
      onOpenChange(next);
    };
    $[8] = dialogStep;
    $[9] = onOpenChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[11] !== reset || $[12] !== setSelectedWalletId) {
    t6 = function resetState() {
      setDialogStep("input");
      setSelectedWalletId("");
      setRecipient("");
      setAmount("");
      setMaxAmountD6(null);
      setInputError(null);
      reset();
    };
    $[11] = reset;
    $[12] = setSelectedWalletId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const resetState = t6;
  let t7;
  if ($[14] !== t || $[15] !== wallets) {
    t7 = function validateAddress(value) {
      if (value.trim().length === 0) {
        return null;
      }
      if (!BASE58_REGEX.test(value)) {
        return t("error_invalid_address");
      }
      try {
        new PublicKey(value);
      } catch {
        return t("error_invalid_address");
      }
      if (wallets.some(w => w.address === value)) {
        return t("error_own_address");
      }
      return null;
    };
    $[14] = t;
    $[15] = wallets;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const validateAddress = t7;
  let t8;
  if ($[17] !== balanceD6 || $[18] !== formatBalance || $[19] !== maxAmountD6 || $[20] !== t || $[21] !== toUsdc) {
    t8 = function validateAmount(value_0) {
      if (value_0.trim().length === 0) {
        return null;
      }
      const amountD6 = maxAmountD6 ?? parseLocalAmountD6(value_0, toUsdc);
      if (amountD6 === null) {
        return t("error_invalid_amount");
      }
      if (amountD6 < BigInt(USDC_DUST_THRESHOLD_D6)) {
        return t("error_min_amount", {
          min: formatBalance(USDC_DUST_THRESHOLD)
        });
      }
      if (amountD6 > balanceD6) {
        return t("error_insufficient_balance");
      }
      return null;
    };
    $[17] = balanceD6;
    $[18] = formatBalance;
    $[19] = maxAmountD6;
    $[20] = t;
    $[21] = toUsdc;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const validateAmount = t8;
  let t9;
  if ($[23] !== amount || $[24] !== recipient || $[25] !== selectedWallet || $[26] !== validateAddress || $[27] !== validateAmount) {
    t9 = function isFormValid() {
      if (!selectedWallet) {
        return false;
      }
      const trimmedRecipient = recipient.trim();
      const trimmedAmount = amount.trim();
      if (trimmedRecipient.length === 0 || trimmedAmount.length === 0) {
        return false;
      }
      if (validateAddress(trimmedRecipient) !== null) {
        return false;
      }
      if (validateAmount(trimmedAmount) !== null) {
        return false;
      }
      return true;
    };
    $[23] = amount;
    $[24] = recipient;
    $[25] = selectedWallet;
    $[26] = validateAddress;
    $[27] = validateAmount;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const isFormValid = t9;
  let t10;
  if ($[29] !== amount || $[30] !== recipient || $[31] !== validateAddress || $[32] !== validateAmount) {
    t10 = async function handleContinue(e) {
      e.preventDefault();
      const addrError = validateAddress(recipient.trim());
      const amtError = validateAmount(amount.trim());
      if (addrError !== null) {
        setInputError(addrError);
        return;
      }
      if (amtError !== null) {
        setInputError(amtError);
        return;
      }
      setDialogStep("confirm");
    };
    $[29] = amount;
    $[30] = recipient;
    $[31] = validateAddress;
    $[32] = validateAmount;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const handleContinue = t10;
  let t11;
  if ($[34] !== amount || $[35] !== currency || $[36] !== maxAmountD6 || $[37] !== recipient || $[38] !== selectedWallet || $[39] !== submit || $[40] !== t || $[41] !== toUsdc) {
    t11 = async function handleConfirm() {
      if (!selectedWallet) {
        return;
      }
      const amountD6_0 = maxAmountD6 ?? parseLocalAmountD6(amount, toUsdc);
      if (amountD6_0 === null) {
        reportError(new Error("transfer_usdc_amount_conversion_invalid"), {
          component: "TransferFunds",
          currency,
          network: getActiveNetwork()
        });
        setInputError(t("error_invalid_amount"));
        setDialogStep("input");
        return;
      }
      setDialogStep("processing");
      await submit(selectedWallet.id, selectedWallet.address, selectedWallet.type, recipient.trim(), amountD6_0);
    };
    $[34] = amount;
    $[35] = currency;
    $[36] = maxAmountD6;
    $[37] = recipient;
    $[38] = selectedWallet;
    $[39] = submit;
    $[40] = t;
    $[41] = toUsdc;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const handleConfirm = t11;
  let t12;
  if ($[43] !== reset) {
    t12 = function handleTryAgain() {
      reset();
      setDialogStep("input");
    };
    $[43] = reset;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const handleTryAgain = t12;
  const handleMaxAmount = function handleMaxAmount() {
    if (balance > 0) {
      setAmount(formatInputAmount(balance));
      setMaxAmountD6(balanceD6);
      setInputError(null);
    }
  };
  let t13;
  if ($[45] !== setSelectedWalletId) {
    t13 = function handleWalletChange(walletId) {
      setSelectedWalletId(walletId);
      setAmount("");
      setMaxAmountD6(null);
      setInputError(null);
    };
    $[45] = setSelectedWalletId;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const handleWalletChange = t13;
  let t14;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = function handleRecipientChange(value_1) {
      setRecipient(value_1);
      setInputError(null);
    };
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const handleRecipientChange = t14;
  let t15;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = function handleAmountChange(value_2) {
      setAmount(value_2);
      setMaxAmountD6(null);
      setInputError(null);
    };
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const handleAmountChange = t15;
  let t16;
  let t17;
  if ($[49] !== announce || $[50] !== t || $[51] !== txStep) {
    t16 = () => {
      if (txStep === "submitting") {
        announce(t("sending"));
      }
      if (txStep === "confirmed") {
        announce(t("success_title"));
      }
      if (txStep === "failed") {
        announce(t("failed_title"));
      }
    };
    t17 = [txStep, announce, t];
    $[49] = announce;
    $[50] = t;
    $[51] = txStep;
    $[52] = t16;
    $[53] = t17;
  } else {
    t16 = $[52];
    t17 = $[53];
  }
  useEffect(t16, t17);
  let t18;
  if ($[54] !== dialogStep || $[55] !== t || $[56] !== txStep) {
    t18 = dialogStep === "confirm" ? t("confirm_title") : dialogStep === "result" ? txStep === "confirmed" ? t("success_title") : t("failed_title") : t("title");
    $[54] = dialogStep;
    $[55] = t;
    $[56] = txStep;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const stepTitle = t18;
  const currentStep = dialogStep === "input" ? 1 : dialogStep === "confirm" ? 2 : 3;
  let t19;
  if ($[58] !== multiWalletEnabled || $[59] !== primaryWallet || $[60] !== wallets) {
    const scopedWallets = multiWalletEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
    const activeWallets = scopedWallets.filter(_temp);
    t19 = activeWallets.map(_temp2);
    $[58] = multiWalletEnabled;
    $[59] = primaryWallet;
    $[60] = wallets;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const walletItems = t19;
  let t20;
  if ($[62] !== dialogStep || $[63] !== handleConfirm || $[64] !== handleOpenChange || $[65] !== handleTryAgain || $[66] !== isFormValid || $[67] !== signature || $[68] !== t || $[69] !== txStep) {
    t20 = dialogStep === "processing" ? undefined : <ModalFooter>{dialogStep === "input" ? <AnimatedButton type="submit" form="transfer-funds-form" disabled={!isFormValid()}>{t("continue")}</AnimatedButton> : null}{dialogStep === "confirm" ? <><AnimatedButton variant="secondary" onClick={() => setDialogStep("input")}>{t("back")}</AnimatedButton><AnimatedButton onClick={handleConfirm}>{t("confirm_send")}</AnimatedButton></> : null}{dialogStep === "result" && txStep === "confirmed" && signature !== null ? <AnimatedButton variant="secondary" onClick={() => {
        window.open(getExplorerUrl(signature), "_blank");
      }}>{t("view_explorer")}</AnimatedButton> : null}{dialogStep === "result" && txStep === "failed" ? <AnimatedButton variant="secondary" onClick={handleTryAgain}>{t("try_again")}</AnimatedButton> : null}{dialogStep === "result" ? <AnimatedButton onClick={() => handleOpenChange(false)}>{t("done")}</AnimatedButton> : null}</ModalFooter>;
    $[62] = dialogStep;
    $[63] = handleConfirm;
    $[64] = handleOpenChange;
    $[65] = handleTryAgain;
    $[66] = isFormValid;
    $[67] = signature;
    $[68] = t;
    $[69] = txStep;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  const stepFooter = t20;
  const T0 = SilkSheet;
  const t21 = dialogStep === "processing";
  const t22 = "page";
  const t23 = "sm";
  let t24;
  if ($[71] !== stepTitle) {
    t24 = <span className="text-heading-md">{stepTitle}</span>;
    $[71] = stepTitle;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== currentStep) {
    t25 = <StepIndicator currentStep={currentStep} totalSteps={3} showCaption={false} />;
    $[73] = currentStep;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t24 || $[76] !== t25) {
    t26 = <div className="flex flex-col gap-3">{t24}{t25}</div>;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  const T1 = MotionAnimatedAutoHeight;
  let t27;
  if ($[78] !== dialogStep || $[79] !== inputError || $[80] !== txStep) {
    t27 = [dialogStep, txStep, inputError];
    $[78] = dialogStep;
    $[79] = inputError;
    $[80] = txStep;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  const t28 = dialogStep === "input" && <TransferInputStep selectedWalletId={selectedWalletId} walletLabel={selectedWallet ? walletDisplayLabel(selectedWallet) : ""} showWalletPicker={multiWalletEnabled} walletItems={walletItems} onWalletChange={handleWalletChange} recipient={recipient} onRecipientChange={handleRecipientChange} amount={amount} onAmountChange={handleAmountChange} inputError={inputError} availableBalanceLabel={t("available_balance", {
    balance: formatBalance(balance, "full")
  })} onMaxAmount={handleMaxAmount} onSubmit={handleContinue} />;
  let t29;
  if ($[82] !== amount || $[83] !== dialogStep || $[84] !== formatLocalAmount || $[85] !== recipient || $[86] !== selectedWallet) {
    t29 = dialogStep === "confirm" && selectedWallet && <TransferConfirmationStep from={selectedWallet.label ?? selectedWallet.address} destinationLabel={truncateAddress(recipient.trim())} amount={formatLocalAmount(Number(amount))} />;
    $[82] = amount;
    $[83] = dialogStep;
    $[84] = formatLocalAmount;
    $[85] = recipient;
    $[86] = selectedWallet;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  let t30;
  if ($[88] !== dialogStep) {
    t30 = dialogStep === "processing" && <TransferProcessingStep />;
    $[88] = dialogStep;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] !== dialogStep || $[91] !== signature || $[92] !== txError || $[93] !== txStep) {
    t31 = dialogStep === "result" && <TransferResultStep step={txStep} error={txError} signature={signature} />;
    $[90] = dialogStep;
    $[91] = signature;
    $[92] = txError;
    $[93] = txStep;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  let t32;
  if ($[95] !== T1 || $[96] !== t27 || $[97] !== t28 || $[98] !== t29 || $[99] !== t30 || $[100] !== t31) {
    t32 = <T1 layoutDependencies={t27}>{t28}{t29}{t30}{t31}</T1>;
    $[95] = T1;
    $[96] = t27;
    $[97] = t28;
    $[98] = t29;
    $[99] = t30;
    $[100] = t31;
    $[101] = t32;
  } else {
    t32 = $[101];
  }
  let t33;
  if ($[102] !== T0 || $[103] !== handleOpenChange || $[104] !== open || $[105] !== resetState || $[106] !== stepFooter || $[107] !== stepTitle || $[108] !== t21 || $[109] !== t26 || $[110] !== t32) {
    t33 = <T0 open={open} onOpenChange={handleOpenChange} onAfterClose={resetState} guardDismiss={t21} variant={t22} size={t23} title={stepTitle} header={t26} footer={stepFooter}>{t32}</T0>;
    $[102] = T0;
    $[103] = handleOpenChange;
    $[104] = open;
    $[105] = resetState;
    $[106] = stepFooter;
    $[107] = stepTitle;
    $[108] = t21;
    $[109] = t26;
    $[110] = t32;
    $[111] = t33;
  } else {
    t33 = $[111];
  }
  return t33;
}
function _temp2(w_0) {
  return {
    value: w_0.id,
    label: walletDisplayLabel(w_0)
  };
}
function _temp(wallet) {
  return wallet.archivedAt === null && wallet.isAvailable !== false;
}